import { useState, useEffect, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../store/authStore";
import SEO from "../components/SEO";

export default function RegisterPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [dob, setDob] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [ageStatus, setAgeStatus] = useState<"unknown" | "blocked" | "needs_consent" | "allowed">("unknown");
  const { register, loading, error, clearError, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (user) navigate("/dashboard", { replace: true }); }, [user, navigate]);

  // Check age when DOB changes
  useEffect(() => {
    if (!dob) { setAgeStatus("unknown"); return; }
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    if (age < 13) setAgeStatus("blocked");
    else if (age < 16) setAgeStatus("needs_consent");
    else setAgeStatus("allowed");
  }, [dob]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirmPassword) return;
    if (ageStatus === "blocked") return;
    if (ageStatus === "needs_consent" && !parentEmail) return;
    try {
      await register(email, password);
      // After registration, submit age verification
      const { api } = await import("../services/api");
      const result = await api.post<{ status: string; message: string }>("/auth/verify-age", {
        dateOfBirth: dob,
        parentEmail: ageStatus === "needs_consent" ? parentEmail : undefined,
      });
      if (result.status === "pending_consent") {
        navigate("/pending-consent");
      } else {
        navigate("/onboarding");
      }
    } catch { /* error in store */ }
  }

  const passwordMismatch = confirmPassword.length > 0 && password !== confirmPassword;

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      <SEO title="Create Account" description="Start your free 7-day trial of Madrich — personalized college admissions roadmap, essay coaching, and 24/7 AI guidance. No credit card required." canonical="/register" />
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-32 right-16 w-72 h-72 border border-white/10 rounded-full" />
          <div className="absolute bottom-16 left-16 w-56 h-56 border border-white/10 rounded-full" />
          <div className="absolute top-1/3 left-1/2 w-40 h-40 border border-white/5 rounded-full" />
        </div>
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <Link to="/" className="text-3xl font-bold mb-8">Madrich</Link>
          <h2 className="text-4xl font-bold leading-tight mb-4">
            Start your journey<br />in under 5 minutes
          </h2>
          <p className="text-indigo-200 text-lg leading-relaxed max-w-md mb-10">
            Answer 5 quick questions and get your personalized college admissions roadmap instantly.
          </p>
          <div className="space-y-4">
            {[
              "Personalized roadmap from day one",
              "AI essay coach — guides, never writes for you",
              "Search 1,900+ colleges and grad programs with fit scores",
              "7 days free, no credit card required",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">&#10003;</span>
                <span className="text-indigo-100">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden text-2xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent mb-8 block">
            Madrich
          </Link>

          <h1 className="text-3xl font-bold text-slate-900 mb-2">Create your account</h1>
          <p className="text-slate-500 mb-8">Start your college admissions journey with Madrich</p>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm flex items-center justify-between">
              {error}
              <button onClick={clearError} className="text-red-400 hover:text-red-600 ml-2">&times;</button>
            </div>
          )}

          <a
            href="/api/auth/google"
            className="w-full flex items-center justify-center gap-3 border-2 border-slate-200 py-3 rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all font-medium text-slate-700 mb-6"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            Continue with Google
          </a>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
            <div className="relative flex justify-center text-xs"><span className="px-3 bg-white text-slate-500 uppercase tracking-wide">or sign up with email</span></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all bg-slate-50 focus:bg-white"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all bg-slate-50 focus:bg-white"
                placeholder="Min 8 characters"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm password</label>
              <input
                type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 outline-none transition-all bg-slate-50 focus:bg-white ${
                  passwordMismatch ? "border-red-300 focus:ring-red-500 focus:border-red-500" : "border-slate-200 focus:ring-indigo-500 focus:border-indigo-500"
                }`}
                placeholder="Repeat your password"
              />
              {passwordMismatch && <p className="text-red-600 text-xs mt-1.5">Passwords don&apos;t match</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Date of birth</label>
              <input
                type="date" value={dob} onChange={(e) => setDob(e.target.value)} required
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all bg-slate-50 focus:bg-white"
              />
              {ageStatus === "blocked" && (
                <p className="text-red-600 text-xs mt-1.5">You must be at least 13 years old to use Madrich.</p>
              )}
              {ageStatus === "needs_consent" && (
                <p className="text-amber-600 text-xs mt-1.5">Since you&apos;re under 16, we need a parent&apos;s email to activate your account.</p>
              )}
            </div>
            {ageStatus === "needs_consent" && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Parent or guardian email</label>
                <input
                  type="email" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} required
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all bg-slate-50 focus:bg-white"
                  placeholder="parent@example.com"
                />
                <p className="text-xs text-slate-500 mt-1">We&apos;ll send them a consent request. Your account activates when they approve.</p>
              </div>
            )}
            <button
              type="submit" disabled={loading || passwordMismatch || ageStatus === "blocked" || (ageStatus === "needs_consent" && !parentEmail)}
              className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white py-3 rounded-xl font-medium hover:shadow-lg hover:shadow-indigo-500/25 disabled:opacity-50 transition-all duration-300"
            >
              {loading ? "Creating account..." : "Create account"}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/login" className="text-indigo-600 font-semibold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
